import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Helmet } from 'react-helmet-async';
import { ArrowLeft, ArrowRight, Bot } from 'lucide-react';
import { ROBOTICS_PRODUCTS } from '../lib/roboticsProducts';
import AeoPageHead from '../components/AeoPageHead';
import { ROBOTICS_CATALOG_PAGE_FAQS } from '../lib/aeo/faqs';

export default function RoboticsPage() {
  return (
    <div
      id="robotics"
      data-avatar-target="robotics"
      className="relative min-h-screen bg-black text-white"
    >
      <Helmet>
        <title>Robotics — SELF Labs</title>
        <meta
          name="description"
          content="Humanoid and quadruped robots from SELF Labs — SELF Bot X1, SELF Bot P1, SELF Dog P1, and SELF Dog E1."
        />
      </Helmet>
      <AeoPageHead path="/robotics" faqs={ROBOTICS_CATALOG_PAGE_FAQS} />

      <div className="pointer-events-none absolute inset-0 overflow-hidden">
        <div className="absolute -top-32 left-1/2 h-96 w-96 -translate-x-1/2 rounded-full bg-indigo-600/15 blur-3xl" />
        <div className="absolute bottom-0 right-0 h-80 w-80 rounded-full bg-cyan-600/10 blur-3xl" />
      </div>

      <div className="relative px-4 pb-24 pt-28 sm:px-6 lg:px-8">
        <div className="mx-auto max-w-6xl">
          <Link
            to="/products"
            className="group inline-flex items-center text-sm text-white/50 transition-colors hover:text-white"
          >
            <ArrowLeft className="mr-2 h-4 w-4 transition-transform group-hover:-translate-x-0.5" />
            Back to Products
          </Link>

          <motion.div
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
            className="mt-10 text-center"
          >
            <span className="inline-flex items-center gap-2 rounded-full border border-white/15 bg-white/5 px-4 py-1.5 text-xs font-medium text-white/80">
              <Bot className="h-3.5 w-3.5" />
              SELF Labs Robotics
            </span>
            <h1 className="mt-6 text-4xl font-bold tracking-tight sm:text-5xl">
              Humanoid & quadruped platforms
            </h1>
            <p className="mx-auto mt-4 max-w-2xl text-lg leading-relaxed text-white/60">
              AI-ready humanoid and quadruped robots for enterprise pilots, education, demonstrations,
              robotics labs, and real-world automation — with optional SELF Labs software, agents, and
              deployment packages.
            </p>
          </motion.div>

          <div className="mt-14 grid gap-8 md:grid-cols-2">
            {ROBOTICS_PRODUCTS.map((product, index) => (
              <motion.div
                key={product.id}
                initial={{ opacity: 0, y: 16 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.45, delay: 0.1 + index * 0.08 }}
              >
                <Link
                  to={`/robotics/${product.id}`}
                  className="group flex h-full flex-col overflow-hidden rounded-3xl border border-white/12 bg-white/[0.03] transition hover:border-white/25 hover:bg-white/[0.05]"
                >
                  <div className="flex min-h-[300px] items-center justify-center bg-gradient-to-b from-white/[0.04] to-black px-6 py-8 sm:min-h-[340px] sm:px-8 sm:py-10">
                    <img
                      src={product.images[0].src}
                      alt={product.images[0].alt}
                      className="max-h-[300px] w-full object-contain object-center transition duration-300 group-hover:scale-[1.02] sm:max-h-[340px]"
                    />
                  </div>
                  <div className="flex flex-1 flex-col p-6 sm:p-8">
                    <div className="flex flex-wrap items-start justify-between gap-3">
                      <div>
                        <h2 className="text-2xl font-bold transition group-hover:text-indigo-100">
                          {product.name}
                        </h2>
                        <p className="mt-2 text-sm text-indigo-200/75">{product.tagline}</p>
                      </div>
                      <p className="text-xl font-semibold">{product.priceLabel}</p>
                    </div>
                    <p className="mt-4 flex-1 text-sm leading-relaxed text-white/60 line-clamp-4">
                      {product.shortDescription}
                    </p>
                    <span className="mt-6 inline-flex items-center gap-2 rounded-xl bg-gradient-to-r from-indigo-500 to-cyan-500 px-5 py-2.5 text-sm font-semibold text-white transition group-hover:from-indigo-400 group-hover:to-cyan-400">
                      View details
                      <ArrowRight className="h-4 w-4 transition group-hover:translate-x-0.5" />
                    </span>
                  </div>
                </Link>
              </motion.div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}
